import React, { useState, useEffect } from 'react';
import { Save, Building, Globe, Shield, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SystemSetting } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import LogoUpload from './LogoUpload';
import BlackWhiteLogoUpload from './BlackWhiteLogoUpload';

interface SystemInfo {
  version: string;
  database: {
    type: string;
    version: string;
    host: string;
  };
  nodeVersion: string;
}

const SettingsForm: React.FC = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [blackWhiteLogoPath, setBlackWhiteLogoPath] = useState<string | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchSystemInfo();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      const response = await apiService.get<SystemInfo>('/system/info');
      setSystemInfo(response);
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const settingsResponse = await apiService.get<SystemSetting[]>('/system');
      setSettings(settingsResponse || []);
      
      // Logo-Abfrage separat behandeln, da 404 normal ist
      try {
        const logoSetting = await apiService.get<SystemSetting>('/system/company_logo');
        setLogoPath(logoSetting?.value || null);
      } catch (logoError: any) {
        // 404-Fehler sind normal, wenn noch kein Logo gesetzt wurde
        if (logoError.response?.status === 404) {
          console.log('No logo setting found yet');
          setLogoPath(null);
        } else {
          console.error('Error fetching logo setting:', logoError);
          setLogoPath(null);
        }
      }

      // Schwarz-Weiß-Logo-Abfrage separat behandeln, da 404 normal ist
      try {
        const blackWhiteLogoSetting = await apiService.get<SystemSetting>('/system/logo-bw');
        setBlackWhiteLogoPath(blackWhiteLogoSetting?.value || null);
      } catch (blackWhiteLogoError: any) {
        // 404-Fehler sind normal, wenn noch kein Schwarz-Weiß-Logo gesetzt wurde
        if (blackWhiteLogoError.response?.status === 404) {
          console.log('No black & white logo setting found yet');
          setBlackWhiteLogoPath(null);
        } else {
          console.error('Error fetching black & white logo setting:', blackWhiteLogoError);
          setBlackWhiteLogoPath(null);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
      setSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => 
      prev.map(setting => 
        setting.key === key ? { ...setting, value } : setting
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Speichere nur geänderte Einstellungen
      const updatePromises = settings.map(async (setting) => {
        try {
          await apiService.put(`/system/${setting.key}`, { value: setting.value });
          console.log(`Setting ${setting.key} updated successfully`);
        } catch (error) {
          console.error(`Error updating setting ${setting.key}:`, error);
          throw error;
        }
      });
      
      await Promise.all(updatePromises);
      toast.success('Einstellungen erfolgreich gespeichert');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (newLogoPath: string | null) => {
    setLogoPath(newLogoPath);
  };

  const handleBlackWhiteLogoChange = (newLogoPath: string | null) => {
    setBlackWhiteLogoPath(newLogoPath);
  };

  const getSettingIcon = (key: string) => {
    const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
      company_name: Building,
      company_logo: Building,
      default_language: Globe,
      max_file_size: Database,
    };
    return iconMap[key] || Shield;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Settings - nur anzeigen wenn es non-logo Settings gibt */}
      {settings.filter(setting => 
        setting.key.startsWith('company_') && 
        setting.key !== 'company_logo' && 
        setting.key !== 'logo_bw'
      ).length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Unternehmens-Einstellungen</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Konfigurieren Sie Ihre Unternehmens-Daten
                </p>
              </div>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {settings
                .filter(setting => 
                  setting.key.startsWith('company_') && 
                  setting.key !== 'company_logo' && 
                  setting.key !== 'logo_bw'
                )
                .map(setting => {
                  const Icon = getSettingIcon(setting.key);
                  return (
                    <div key={setting.key} className="space-y-2">
                      <Label htmlFor={setting.key} className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {setting.description || setting.key}
                      </Label>
                      <Input
                        id={setting.key}
                        value={setting.value}
                        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                        placeholder={setting.description}
                      />
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logo Upload */}
      <LogoUpload 
        currentLogoPath={logoPath || undefined} 
        onLogoChange={handleLogoChange} 
      />

      {/* Schwarz-Weiß-Logo Upload */}
      <BlackWhiteLogoUpload 
        currentLogoPath={blackWhiteLogoPath || undefined} 
        onLogoChange={handleBlackWhiteLogoChange} 
      />



      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            System-Informationen
          </CardTitle>
        </CardHeader>
        <CardContent>
          {systemInfo ? (
            <div className="grid gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Version:</span>
                <span className="font-mono">{systemInfo.version}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Build:</span>
                <span>2025-01-18</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Node.js:</span>
                <span className="font-mono">{systemInfo.nodeVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Datenbank:</span>
                <span className="font-mono">
                  {systemInfo.database.type} {systemInfo.database.version}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">DB Host:</span>
                <span className="font-mono">{systemInfo.database.host}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsForm;
